import React from "react";
import { AlertTriangle } from "lucide-react";

// =====================================================================
// PopularAuditBanner — read-only informational banner surfacing services
// with more than one `popular=true` entry in the same list (tiers /
// addons / standalone_items / kits). The recommended-item design language
// only reads correctly when exactly ONE entry per list is spotlighted;
// this catches drift without silently fixing it.
//
// Scope: audits whatever services are loaded in admin state. The public
// /api/services endpoint hides inactive/invisible services, so those
// won't be caught until re-activated — acceptable since they aren't
// rendering the popular treatment to visitors anyway.
// =====================================================================
const POPULAR_ARRAYS = [
  { key: "tiers",            label: "tier"  },
  { key: "kits",             label: "kit"   },
  { key: "standalone_items", label: "item"  },
  { key: "addons",           label: "add-on"},
];

const auditPopularConflicts = (services) => {
  const conflicts = [];
  for (const s of services || []) {
    const per = [];
    for (const { key, label } of POPULAR_ARRAYS) {
      const arr = s[key] || [];
      const count = arr.filter((x) => x && x.popular === true).length;
      if (count > 1) per.push({ key, label, count });
    }
    if (per.length > 0) conflicts.push({ service: s, per });
  }
  return conflicts;
};

export const PopularAuditBanner = ({ services }) => {
  const conflicts = auditPopularConflicts(services);
  if (conflicts.length === 0) return null;
  return (
    <div
      data-testid="popular-audit-banner"
      className="mb-4 rounded-xl border border-amber-500/30 bg-amber-500/[0.06] px-4 py-3 flex items-start gap-3"
    >
      <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" strokeWidth={2} />
      <div className="min-w-0">
        <div className="text-xs font-bold uppercase tracking-[0.18em] text-amber-400">
          Popular flag conflicts detected
        </div>
        <p className="text-xs text-[#B5B5B5] mt-1 leading-relaxed">
          One or more services have more than one entry marked <span className="text-white font-semibold">popular</span> in the same list — only one item at a time can carry the "recommended" visual treatment, so the spotlight will read ambiguously to visitors. Open the service in the editor to leave exactly one popular per list.
        </p>
        <ul className="mt-2 space-y-1">
          {conflicts.map(({ service, per }) => (
            <li
              key={service.id}
              data-testid={`popular-audit-row-${service.slug}`}
              className="text-xs text-[#D1D1D1]"
            >
              <span className="font-semibold text-white">{service.name}</span>
              <span className="text-[#7A7A7A]"> — </span>
              {per.map((p, i) => (
                <span key={p.key}>
                  {i > 0 && <span className="text-[#7A7A7A]">, </span>}
                  <span className="text-amber-400 font-semibold">{p.count}</span>
                  <span className="text-[#A3A3A3]"> popular {p.label}{p.count === 1 ? "" : "s"}</span>
                </span>
              ))}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
